/**
 * @file        src/pages/wallet/WalletActionModal.jsx
 * @description Add money / refund to a user's wallet. A two-step flow: step 1
 *              collects amount/expiry-or-reference/reason, step 2 is a distinct
 *              password-confirmation screen (server re-verifies too either way).
 *              Credit takes an optional expiry + a mandatory reason; refund takes
 *              an optional booking reference (its expiry is inherited server-side).
 * @author      Kinetika Labs
 * @created     2026-07-23
 * @changelog
 *   - 2026-09-16 Claude — Password fields carry autoComplete="new-password" +
 *                          data-lpignore / data-1p-ignore, so the browser and
 *                          password managers stop pre-filling the signed-in
 *                          admin's own password into them. Only Login.jsx keeps
 *                          "current-password".
 *   - 2026-07-23 Claude — Initial (Admin Wallet Management).
 *   - 2026-07-24 Claude — Use the themed DatePicker for the credit expiry date
 *                          (was the native <input type="date">) to match the UI.
 *   - 2026-07-27 Claude — Split into a details step + a separate password
 *                          confirmation step (was one combined form).
 *   - 2026-08-04 Claude — Credit expiry DatePicker gets min=today: a past
 *                          expiry is rejected server-side, so block it here.
 *   - 2026-08-04 Claude — Refund cap errors (unknown ref / amount over the
 *                          booking's refundable balance) come back on details-
 *                          step fields, so surface them there: show the
 *                          bookingRef field error and jump back from the
 *                          password step when a non-password field fails.
 *   - 2026-08-10 Claude — Accepts `pooled`/`companyName` (a B2B user has no
 *                          personal wallet) and shows a warning that this
 *                          credits/refunds the shared company wallet, not just
 *                          this one employee.
 *   - 2026-09-12 Claude — `pooled`/`companyName` removed with their warning:
 *                          the per-user wallet screens are retail-only now, so
 *                          this modal can never be opened on a company pool.
 *                          Company money is credited from Company Wallets,
 *                          which has its own password-confirmed form.
 *   - 2026-09-15 Claude — Reason opts out of browser/extension autofill, so it
 *                          can never open pre-filled with a saved email.
 *   - 2026-09-17 Claude — The re-auth field is now <PasswordInput> (reveal
 *                          toggle) — this one gates a real money movement.
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Icon from '../../components/Icon';
import DatePicker from '../../components/DatePicker';
import { toast } from '../../lib/toast';
import { creditWallet, refundWallet } from '../../api/wallets';
import PasswordInput from '../../components/PasswordInput';

const inr = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const todayKey = () => {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
};

export default function WalletActionModal({ mode = 'credit', user, onClose }) {
  const qc = useQueryClient();
  const isCredit = mode === 'credit';
  const [step, setStep] = useState('details'); // 'details' | 'confirm'
  const [amount, setAmount] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [bookingRef, setBookingRef] = useState('');
  const [reason, setReason] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErr, setFieldErr] = useState({});
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const continueToConfirm = (e) => {
    e.preventDefault();
    setErr(''); setFieldErr({});
    const amt = Number(amount);
    if (!(amt > 0)) { setFieldErr({ amount: 'Enter an amount greater than zero.' }); return; }
    if (isCredit && expiryDate && expiryDate < todayKey()) { setFieldErr({ expiryDate: 'Expiry date cannot be in the past.' }); return; }
    if (isCredit && !reason.trim()) { setFieldErr({ reason: 'A reason is required.' }); return; }
    setStep('confirm');
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(''); setFieldErr({});
    const amt = Number(amount);
    if (!password) { setFieldErr({ password: 'Your password is required.' }); return; }

    setBusy(true);
    try {
      const body = isCredit
        ? { amount: amt, expiryDate: expiryDate || '', reason: reason.trim(), password }
        : { amount: amt, bookingRef: bookingRef.trim(), reason: reason.trim(), password };
      const res = isCredit ? await creditWallet(user.id, body) : await refundWallet(user.id, body);
      toast.ok(isCredit ? 'Money added' : 'Refund posted', `New balance ${inr(res?.data?.balance)}`);
      qc.invalidateQueries({ queryKey: ['wallet'] });
      onClose();
    } catch (e2) {
      const apiErr = e2?.response?.data?.error;
      if (apiErr?.code === 'BAD_CREDENTIALS') { setFieldErr({ password: 'Incorrect password.' }); }
      else if (apiErr?.fields) {
        setFieldErr(apiErr.fields);
        setErr(apiErr?.message || '');
        // Field errors other than password belong to inputs on the details
        // step — go back there so the admin can actually see and fix them.
        if (Object.keys(apiErr.fields).some((k) => k !== 'password')) setStep('details');
      }
      else { setErr(apiErr?.message || 'Something went wrong. Please try again.'); }
    } finally {
      setBusy(false);
    }
  };

  const amt = Number(amount) || 0;

  return (
    <div className="lk-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="lk-modal panel glass" style={{ maxWidth: 460 }}>
        <div className="lk-modal-head">
          <h3>{step === 'confirm' ? 'Confirm with your password' : (isCredit ? 'Add money to wallet' : 'Refund to wallet')}</h3>
          <button className="ib" aria-label="Close" onClick={onClose}><Icon name="x" /></button>
        </div>

        {step === 'details' ? (
          <form className="det-body" onSubmit={continueToConfirm}>
            <p className="muted">
              {isCredit ? 'Crediting' : 'Refunding'} <b>{`${user.firstName} ${user.lastName}`.trim() || user.email}</b>
            </p>

            <div className="field full">
              <label>Amount (₹)</label>
              <input type="number" min="1" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" autoFocus />
              {fieldErr.amount ? <div className="field-error">{fieldErr.amount}</div> : null}
            </div>

            {isCredit ? (
              <div className="field full">
                <label>Expiry date <span className="muted">(optional — blank = never expires)</span></label>
                <DatePicker value={expiryDate} onChange={(v) => setExpiryDate(v)} min={todayKey()} />
                {fieldErr.expiryDate ? <div className="field-error">{fieldErr.expiryDate}</div> : null}
              </div>
            ) : (
              <div className="field full">
                <label>Booking reference <span className="muted">(optional — inherits its expiry, caps the amount at what the booking paid)</span></label>
                <input value={bookingRef} onChange={(e) => setBookingRef(e.target.value)} placeholder="e.g. TJS1180…" />
                {fieldErr.bookingRef ? <div className="field-error">{fieldErr.bookingRef}</div> : null}
              </div>
            )}

            <div className="field full">
              <label>Reason {isCredit ? '' : <span className="muted">(optional)</span>}</label>
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                name="reason"
                autoComplete="off"
                data-lpignore="true"
                data-1p-ignore
                placeholder={isCredit ? 'e.g. Goodwill credit / promo' : 'e.g. Cancellation refund'}
              />
              {fieldErr.reason ? <div className="field-error">{fieldErr.reason}</div> : null}
            </div>

            {err ? <div className="field-error">{err}</div> : null}

            <div className="form-actions">
              <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn primary">Continue</button>
            </div>
          </form>
        ) : (
          <form className="det-body" onSubmit={submit}>
            <p className="muted">
              {isCredit ? 'Adding' : 'Refunding'} <b>{inr(amt)}</b> {isCredit ? 'to' : 'for'} <b>{`${user.firstName} ${user.lastName}`.trim() || user.email}</b>
              {reason.trim() ? <> — <i>{reason.trim()}</i></> : null}
            </p>

            <div className="field full">
              <label>Confirm your password</label>
              <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your admin password" autoComplete="new-password" data-lpignore="true" data-1p-ignore autoFocus />
              {fieldErr.password ? <div className="field-error">{fieldErr.password}</div> : null}
            </div>

            {err ? <div className="field-error">{err}</div> : null}

            <div className="form-actions">
              <button type="button" className="btn ghost" onClick={() => { setStep('details'); setPassword(''); setErr(''); setFieldErr({}); }} disabled={busy}>Back</button>
              <button type="submit" className="btn primary" disabled={busy}>
                {busy ? 'Please wait…' : (isCredit ? 'Add money' : 'Post refund')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
